import { clsx, type WithClassName } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import type React from "react";
import { RegrelloLinkify } from "../../text/linkify/RegrelloLinkify";
import type { RegrelloIntent } from "../../tokens/enums/regrelloIntent";
import { RegrelloSize } from "../../tokens/enums/regrelloSize";
import { RegrelloIcon, type RegrelloIconName } from "../../visual/icons/RegrelloIcon";
import { RegrelloTooltip, type RegrelloTooltipProps } from "./RegrelloTooltip";

export interface RegrelloTooltippedInfoIconProps extends Pick<RegrelloTooltipProps, "align">, WithClassName {
  /** The name of the tooltip triggering icon. */
  iconName?: RegrelloIconName;

  /**
   * Whether this element should render inline with other text.
   *
   * @default false
   */
  inline?: boolean;

  /**
   * The intent to apply to this icon.
   *
   * @default undefined
   */
  intent?: RegrelloIntent;

  /**
   * Whether the default left margin of 4px is omitted. The margin is included by default because
   * usually this icon is rendered to the right of some text.
   *
   * @default false
   */
  isDefaultMarginsOmitted?: boolean;

  /** The text to display in the tooltip on hover. */
  tooltipText?: React.ReactNode;

  /**
   * The visual treatment to give to the tooltip.
   * - `"default"`: A standard dark-background tooltip.
   * - `"popover"`: A light-background tooltip with a border.
   *
   * @default "default"
   */
  variant?: "default" | "popover";
}

/**
 * An info icon with an on-hover tooltip displaying some provided text.
 */
export function RegrelloTooltippedInfoIcon({
  align = "start",
  className,
  iconName = "info",
  inline: isInline = false,
  intent,
  isDefaultMarginsOmitted = false,
  tooltipText,
  variant = "default",
}: RegrelloTooltippedInfoIconProps) {
  const icon = (
    <RegrelloIcon
      className={clsx(
        "cursor-help text-neutral-icon hover:text-neutral-iconHovered",
        { "ml-1": !isDefaultMarginsOmitted },
        className,
      )}
      dataTestId={DataTestIds.INFO_ICON}
      displayInline={true}
      iconName={iconName}
      intent={intent}
      size={RegrelloSize.X_SMALL}
    />
  );

  return tooltipText == null || (typeof tooltipText === "string" && tooltipText.length === 0) ? null : (
    <RegrelloTooltip
      align={align}
      content={<RegrelloLinkify>{tooltipText}</RegrelloLinkify>}
      enableHoverableContent={variant === "popover"}
      maxWidth={400}
      side="top"
      variant={variant}
    >
      {isInline ? <span>{icon}</span> : <span className="flex items-center">{icon}</span>}
    </RegrelloTooltip>
  );
}
